import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { apiFetch } from "../../../utils/apiFetch";
import { API_URL, COUNTRY_CONFIG } from "../constants";
import { C } from "../styles";
import { authHeaders } from "../helpers";
import { SectionHead } from "../components/ui";
import WorkLocationsTable from "../components/WorkLocationsTable";
import WorkLocationDrawer, { LocForm } from "../components/WorkLocationDrawer";
import DeleteConfirmModal from "../components/DeleteConfirmModal";
import type { WorkLocation, CountryCode } from "../types";

interface Props {
  businessCountry?: CountryCode | string;
}

export default function WorkLocationsSection({ businessCountry = "CA" }: Props) {
  const [locations, setLocations] = useState<WorkLocation[]>([]);
  const [companyName, setCompanyName] = useState("");
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | number | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | number | null>(null);
  const [confirmIntlChange, setConfirmIntlChange] = useState(false);
  const [saving, setSaving] = useState(false);

  const initialForm: LocForm = {
    name: "", street_address: "", suite: "", municipality: "", province: "",
    postal_code: "", phone: "", country: String(businessCountry),
    is_primary: false, is_international: false,
  };
  const [form, setForm] = useState<LocForm>(initialForm);
  const [intlMode, setIntlMode] = useState(false);

  const loadLocations = async () => {
    try {
      const res = await apiFetch("/api/v1/work-locations", { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setLocations(data || []);
      }
    } catch (e) {}
    setLoading(false);
  };

  useEffect(() => { loadLocations(); }, []);

  useEffect(() => {
    apiFetch("/api/v1/company/profile", { headers: authHeaders() })
      .then((r: Response) => r.ok ? r.json() : null)
      .then((d: any) => { if (d && d.company_name) setCompanyName(d.company_name); })
      .catch(() => {});
  }, []);

  const openNew = () => {
    setForm({ ...initialForm, country: String(businessCountry), is_international: false });
    setIntlMode(false);
    setEditingId(null);
    setDrawerOpen(true);
  };

  const openEdit = (loc: WorkLocation) => {
    setForm({
      name: loc.name || "",
      street_address: loc.street_address || "",
      suite: loc.suite || "",
      municipality: loc.municipality || "",
      province: loc.province || "",
      postal_code: loc.postal_code || "",
      phone: loc.phone || "",
      country: loc.country || String(businessCountry),
      is_primary: loc.is_primary || false,
      is_international: loc.is_international || false,
    });
    setIntlMode(loc.is_international || (!!loc.country && loc.country.toUpperCase() !== (String(businessCountry) || "CA").toUpperCase()));
    setEditingId(loc.id as string | number);
    setDrawerOpen(true);
  };

  const closeDrawer = () => { setDrawerOpen(false); setEditingId(null); };

  const onSave = async () => {
    if (!form.name?.trim()) { alert("Location name is required"); return; }
    const activeCountry = (intlMode ? form.country : String(businessCountry) || "CA").toUpperCase();
    const activeConfig = (COUNTRY_CONFIG as any)[activeCountry] || (COUNTRY_CONFIG as any).OTHER;
    if (activeConfig.subdivisions && form.province && !activeConfig.subdivisions.includes(form.province)) {
      alert("The selected " + activeConfig.subdivLabel.toLowerCase() + " (" + form.province + ") is not valid for " + activeConfig.name + ". Please pick from the dropdown.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        country: intlMode ? form.country : businessCountry,
        is_international: intlMode,
      };
      const url = editingId
        ? API_URL + "/api/v1/work-locations/" + editingId
        : API_URL + "/api/v1/work-locations";
      const method = editingId ? "PATCH" : "POST";
      const res = await fetch(url, { method, headers: authHeaders(), body: JSON.stringify(payload) });
      if (res.ok) { await loadLocations(); closeDrawer(); }
      else { alert("Could not save location. Please try again."); }
    } catch (e) { alert("Could not save location."); }
    setSaving(false);
  };

  const onDelete = async (id: string | number) => {
    try {
      const res = await apiFetch("/api/v1/work-locations/" + id, { method: "DELETE", headers: authHeaders() });
      if (res.ok || res.status === 204) { await loadLocations(); setConfirmDelete(null); closeDrawer(); }
    } catch (e) {}
  };

  const filtered = locations.filter((loc: WorkLocation) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (loc.name || "").toLowerCase().includes(q)
      || (loc.municipality || "").toLowerCase().includes(q)
      || (loc.province || "").toLowerCase().includes(q)
      || (loc.street_address || "").toLowerCase().includes(q);
  });

  const companyConfig = (COUNTRY_CONFIG as any)[(String(businessCountry) || "CA").toUpperCase()] || (COUNTRY_CONFIG as any).OTHER;

  if (loading) return <div style={{ color: C.ink, fontSize: 13, padding: 20 }}>Loading work locations...</div>;

  return (
    <>
      <SectionHead title="Work locations" subtitle="Physical workplaces where your employees report. Used to determine provincial tax rates, workers' compensation, and where each employee's payroll is processed." />

      <WorkLocationsTable
        companyName={companyName}
        businessCountry={String(businessCountry)}
        totalCount={locations.length}
        filtered={filtered}
        search={search}
        setSearch={setSearch}
        onOpenNew={openNew}
        onOpenEdit={openEdit}
      />

      <WorkLocationDrawer
        open={drawerOpen}
        editingId={editingId}
        form={form}
        setForm={setForm}
        intlMode={intlMode}
        setIntlMode={setIntlMode}
        businessCountry={String(businessCountry)}
        saving={saving}
        onClose={closeDrawer}
        onSave={onSave}
        onRequestDelete={() => editingId && setConfirmDelete(editingId)}
        onRequestIntlChange={() => setConfirmIntlChange(true)}
      />

      <DeleteConfirmModal
        open={confirmDelete !== null}
        title="Delete this location?"
        message="Any employees assigned to this location will need to be reassigned. This action cannot be undone."
        confirmLabel="Delete location"
        deleting={false}
        onConfirm={() => confirmDelete && onDelete(confirmDelete)}
        onCancel={() => setConfirmDelete(null)}
      />

      {confirmIntlChange && createPortal(
        <div onClick={() => setConfirmIntlChange(false)} style={{ position: "fixed", inset: 0, background: "rgba(10,26,30,.42)", zIndex: 2000, display: "grid", placeItems: "center" }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, padding: "24px 28px", maxWidth: 460, width: "90%", boxShadow: "0 24px 60px rgba(0,0,0,.3)" }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, color: C.ink, marginBottom: 8 }}>Change this location to international?</h3>
            <p style={{ fontSize: 13, color: C.ink, marginBottom: 20, lineHeight: 1.55 }}>You are about to change this location from {companyConfig.name} to a different country. The province/state and postal code will need to be re-entered to match the new country.</p>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setConfirmIntlChange(false)} style={{ background: "#fff", color: C.ink, border: "1.5px solid " + C.ink, borderRadius: 6, padding: "9px 16px", fontWeight: 500, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>Keep as {companyConfig.name}</button>
              <button onClick={() => { const newCountry = (String(businessCountry) || "CA").toUpperCase() === "CA" ? "US" : "CA"; setIntlMode(true); setForm({ ...form, country: newCountry, province: "", postal_code: "" }); setConfirmIntlChange(false); }} style={{ background: C.ink, color: "#fff", border: 0, borderRadius: 6, padding: "9px 20px", fontWeight: 500, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>Yes, change to international</button>
            </div>
          </div>
        </div>, document.body)}
    </>
  );
}